import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/ui/chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { SearchLight } from "@/types/Search";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { useMemo } from "react";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@components/ui/button";
import { ChevronDownIcon } from "lucide-react";
import { MentionsOverTimeDay } from "@/api/endpoints/dashboard";

export default function MentionsOverTime({
  hasChangedSearchIds,
  selectedSearchIds,
  onSelectSearchId,
  onDeselectSearchId,
  getSearchColor,
  chartData,
  searches,
}: {
  hasChangedSearchIds: boolean;
  chartData: MentionsOverTimeDay[];
  searches: SearchLight[];
  selectedSearchIds: string[];
  onSelectSearchId: (searchId: string) => void;
  onDeselectSearchId: (searchId: string) => void;
  getSearchColor: (searchId: string) => string;
}) {
  const { t } = useLingui();

  const selectedSearches = useMemo(() => {
    return searches.filter((search) => selectedSearchIds.includes(search.uuid));
  }, [searches, selectedSearchIds]);

  const chartConfig: ChartConfig = selectedSearches.reduce((config, search) => {
    config[search.uuid] = {
      label: search.title,
      color: getSearchColor(search.uuid),
    };

    return config;
  }, {} as ChartConfig);

  const filteredChartData = useMemo(() => {
    return chartData.map((data) => {
      const newData = { ...data };

      selectedSearchIds.forEach((searchId) => {
        newData[searchId] = data[searchId] || 0;
      });

      return newData;
    });
  }, [selectedSearchIds, chartData]);

  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-6 sm:gap-2 space-y-0 border-b py-5 flex-col sm:flex-row">
        <div className="grid flex-1 gap-2">
          <CardTitle>
            <Trans>Omnämnanden över tid</Trans>
          </CardTitle>
          <CardDescription>
            {hasChangedSearchIds ? (
              <Plural
                value={selectedSearchIds.length}
                one="Din valda bevaknings omnämnanden över tid."
                other="Jämför dina # valda bevakningars omnämnanden över tid."
                _0="Inga valda bevakningar."
              />
            ) : (
              <Plural
                value={selectedSearchIds.length}
                one="Din bevaknings omnämnanden över tid."
                other="Jämför de # bevakningarna med flest omnämnanden över tid."
                _0="Inga valda bevakningar."
              />
            )}
          </CardDescription>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger aria-label={t`Välj bevakningar`} asChild>
            <Button
              className="w-full sm:w-52 justify-between sm:ml-auto"
              variant="outline"
            >
              {selectedSearchIds.length > 0 ? (
                <span className="truncate">
                  {selectedSearches.map((search) => search.title).join(", ")}
                </span>
              ) : (
                <Trans>Välj bevakningar</Trans>
              )}
              <ChevronDownIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[var(--radix-dropdown-menu-trigger-width)]"
          >
            {searches.map((search) => {
              const isSelected = selectedSearchIds.includes(search.uuid);
              const isLastSelected = selectedSearchIds.length === 1;

              return (
                <DropdownMenuCheckboxItem
                  key={search.uuid}
                  checked={isSelected}
                  onSelect={(e) => {
                    e.preventDefault();
                  }}
                  onCheckedChange={() => {
                    if (isSelected) {
                      if (isLastSelected) {
                        return;
                      }

                      onDeselectSearchId(search.uuid);
                    } else {
                      onSelectSearchId(search.uuid);
                    }
                  }}
                >
                  {search.title}
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-64 w-full"
        >
          <AreaChart data={filteredChartData}>
            <defs>
              {selectedSearches.map((search) => {
                const stopColor = getSearchColor(search.uuid);

                return (
                  <linearGradient
                    key={search.uuid}
                    id={`fill${search.uuid}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor={stopColor} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={stopColor} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid vertical={false} />
            <YAxis tickLine={false} axisLine={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => {
                    return new Date(value).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });
                  }}
                  indicator="dot"
                />
              }
            />

            {selectedSearches.map((search) => {
              const color = getSearchColor(search.uuid);

              return (
                <Area
                  isAnimationActive={false}
                  key={search.uuid}
                  dataKey={search.uuid}
                  strokeWidth={1.5}
                  type="monotone"
                  fill={`url(#fill${search.uuid})`}
                  stroke={color}
                />
              );
            })}

            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
